/**
 * Blocking approve/decline surface for agent confirmation gates.
 * MUI port of the confirmation card in AutoFormHandler (workflow-handlers).
 */
import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material'
import {
    Science as FlaskIcon,
    Shield as ShieldIcon,
    InfoOutlined as InfoIcon,
    Check as CheckIcon,
    Block as BlockIcon,
} from '@mui/icons-material'
import type { WaitingFormConfirmation } from '../types'

interface ConfirmationApprovalCardProps {
    confirmation: WaitingFormConfirmation
    taskId: string
    stateVersion?: number
    onSubmit: (payload: {
        data: Record<string, unknown>
        state_version?: number
        idempotency_key?: string
    }) => Promise<void> | void
    onCancel?: () => void
}

type ModeChoice = 'dryrun' | 'live'
type EnvChoice = 'dev' | 'staging' | 'prod'
type OnErrorChoice = 'stop' | 'continue'

function mapConfirmation(confirmation: WaitingFormConfirmation) {
    const proposed = (confirmation.proposed_args || {}) as Record<string, unknown>
    const assumptions = Array.isArray(confirmation.assumptions)
        ? confirmation.assumptions
        : []
    const byField: Record<string, { value?: unknown }> = {}
    for (const a of assumptions) {
        if (a && typeof a === 'object' && 'field' in a) {
            byField[String((a as { field: string }).field)] = a as { value?: unknown }
        }
    }
    const has = (k: string) => k in proposed || k in byField

    const showMode = has('allow_writes') || has('default_dry_run') || true
    const showEnv = has('env')
    const showOnError = has('stop_on_error')

    const guessLive =
        proposed.allow_writes === true ||
        proposed.default_dry_run === false ||
        byField.allow_writes?.value === true ||
        byField.default_dry_run?.value === false

    const aiMode: ModeChoice | null = showMode ? (guessLive ? 'live' : 'dryrun') : null
    const aiEnv = showEnv
        ? String(proposed.env ?? byField.env?.value ?? '') || null
        : null
    const aiOnError: OnErrorChoice | null = showOnError
        ? (proposed.stop_on_error ?? byField.stop_on_error?.value) === false
            ? 'continue'
            : 'stop'
        : null

    const systemName =
        String(
            proposed.system_name ||
                confirmation.tool_name ||
                proposed.system ||
                '',
        ) || null
    const taskCount = Array.isArray(proposed.tasks)
        ? proposed.tasks.length
        : typeof (confirmation as { task_count?: number }).task_count === 'number'
          ? (confirmation as { task_count: number }).task_count
          : null

    return { showMode, showEnv, showOnError, aiMode, aiEnv, aiOnError, systemName, taskCount }
}

interface SegmentButtonProps {
    active: boolean
    tone: 'safe' | 'danger' | 'brand'
    onClick: () => void
    disabled?: boolean
    children: React.ReactNode
}

const SegmentButton: React.FC<SegmentButtonProps> = ({
    active,
    tone,
    onClick,
    disabled,
    children,
}) => {
    const color =
        tone === 'danger'
            ? 'var(--error-color, #ef4444)'
            : tone === 'safe'
              ? 'var(--success-color, #22c55e)'
              : 'var(--button-primary-bg, var(--quantum-violet, #6C4CFF))'

    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            sx={{
                flex: 1,
                minHeight: 42,
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                border: active ? '1px solid transparent' : '1px solid var(--card-border, #e2e8f0)',
                color: active ? color : 'var(--main-text-secondary, #64748b)',
                background: active
                    ? `color-mix(in srgb, ${color} 14%, var(--card-bg, #fff))`
                    : 'var(--main-bg-elevated, #f8fafc)',
                boxShadow: active ? `inset 0 0 0 1px color-mix(in srgb, ${color} 45%, transparent)` : 'none',
            }}
        >
            {children}
        </Button>
    )
}

export const ConfirmationApprovalCard: React.FC<ConfirmationApprovalCardProps> = ({
    confirmation,
    taskId,
    stateVersion,
    onSubmit,
}) => {
    const m = useMemo(() => mapConfirmation(confirmation), [confirmation])

    const [mode, setMode] = useState<ModeChoice | null>(null)
    const [env, setEnv] = useState<EnvChoice | null>(null)
    const [onerror, setOnerror] = useState<OnErrorChoice | null>(null)
    const [understood, setUnderstood] = useState(false)
    const [typedName, setTypedName] = useState('')
    const [decided, setDecided] = useState<'approved' | 'declined' | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const isLive = mode === 'live'
    const systemName = m.systemName || 'the target system'
    const needsTyped = isLive && env === 'prod'
    const typedOk = !needsTyped || (m.systemName && typedName.trim() === m.systemName)

    const gateReady =
        !!mode &&
        (!m.showEnv || !!env) &&
        (!m.showOnError || !!onerror) &&
        understood &&
        typedOk &&
        !decided

    const accentColor = isLive
        ? 'var(--error-color, #ef4444)'
        : mode === 'dryrun'
          ? 'var(--success-color, #22c55e)'
          : 'var(--warning-color, #f59e0b)'

    const impactTitle = !mode
        ? 'Confirm the execution mode'
        : mode === 'dryrun'
          ? 'Dry run — no real data will change'
          : 'Live writes — real records will change'

    const envLabel = env ? ` (${env})` : ''
    const impactBody = !mode
        ? 'Pick how this plan should run below and the exact impact on the target system appears here.'
        : mode === 'dryrun'
          ? `Every connector call is simulated against ${systemName}${envLabel}. Nothing is created or modified.`
          : `Executes real connector calls against ${systemName}${envLabel}. Up to ${m.taskCount ?? 'several'} task${m.taskCount === 1 ? '' : 's'} may create or modify real data${env === 'prod' ? ' in production' : ''}.`

    const submit = async (approved: boolean) => {
        if (isSubmitting || decided) return
        if (approved && !gateReady) return
        setIsSubmitting(true)
        const data: Record<string, unknown> = { confirm: approved }
        if (approved) {
            if (m.showMode) {
                data.allow_writes = isLive
                data.default_dry_run = !isLive
            }
            if (m.showEnv && env) data.env = env
            if (m.showOnError && onerror) data.stop_on_error = onerror === 'stop'
        }
        try {
            await onSubmit({
                data,
                state_version: stateVersion,
                idempotency_key: `confirm:${taskId}:${Date.now()}:${Math.random().toString(36).substring(2)}`,
            })
            setDecided(approved ? 'approved' : 'declined')
        } catch {
            setIsSubmitting(false)
        }
    }

    return (
        <Box
            data-testid="confirmation-card"
            sx={{
                overflow: 'hidden',
                borderRadius: '14px',
                background: 'var(--card-bg, #fff)',
                border: '1px solid var(--card-border, #e2e8f0)',
                flexShrink: 0,
                minHeight: 'fit-content',
                animation: 'confApprovalFadeIn 0.3s ease',
                '@keyframes confApprovalFadeIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                },
            }}
        >
            <Box sx={{ height: 3, background: accentColor }} />
            <Box sx={{ p: 2.25 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexWrap: 'wrap',
                        mb: 1.75,
                    }}
                >
                    <Typography
                        sx={{
                            flex: 1,
                            fontSize: 18,
                            fontWeight: 600,
                            color: 'var(--main-text, #1e293b)',
                        }}
                    >
                        Approve this run?
                    </Typography>
                    <Box
                        sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: isLive
                                ? 'var(--error-color, #ef4444)'
                                : mode === 'dryrun'
                                  ? 'var(--success-color, #22c55e)'
                                  : 'var(--warning-color, #f59e0b)',
                            background: isLive
                                ? 'color-mix(in srgb, var(--error-color, #ef4444) 14%, var(--card-bg, #fff))'
                                : mode === 'dryrun'
                                  ? 'color-mix(in srgb, var(--success-color, #22c55e) 14%, var(--card-bg, #fff))'
                                  : 'color-mix(in srgb, var(--warning-color, #f59e0b) 14%, var(--card-bg, #fff))',
                        }}
                    >
                        {isLive ? 'High risk' : mode === 'dryrun' ? 'Safe — dry run' : 'Confirm to assess'}
                    </Box>
                </Box>

                {(confirmation.impact_description || confirmation.confirm_message) && (
                    <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                            display: 'flex',
                            gap: 1.25,
                            p: 1.75,
                            mb: 2,
                            borderRadius: '12px',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            background: 'var(--main-bg-elevated, #f8fafc)',
                        }}
                    >
                        <InfoIcon sx={{ fontSize: 18, color: 'var(--info-color, #3b82f6)', mt: 0.25 }} />
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--main-text, #1e293b)' }}>
                                {confirmation.impact_description || impactTitle}
                            </Typography>
                            {confirmation.confirm_message && (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        mt: 0.5,
                                        color: 'var(--main-text-secondary, #64748b)',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {confirmation.confirm_message}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--main-text-secondary, #64748b)',
                        mb: 1.5,
                    }}
                >
                    Confirm before approving
                </Typography>

                {m.showMode && (
                    <Box sx={{ mb: 1.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--main-text-secondary, #64748b)', mb: 1 }}>
                            Execution mode
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <SegmentButton
                                active={mode === 'dryrun'}
                                tone="safe"
                                disabled={!!decided}
                                onClick={() => setMode('dryrun')}
                            >
                                <FlaskIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                Dry run
                            </SegmentButton>
                            <SegmentButton
                                active={mode === 'live'}
                                tone="danger"
                                disabled={!!decided}
                                onClick={() => setMode('live')}
                            >
                                <ShieldIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                Live writes
                            </SegmentButton>
                        </Box>
                    </Box>
                )}

                {m.showEnv && (
                    <Box sx={{ mb: 1.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--main-text-secondary, #64748b)', mb: 1 }}>
                            Target environment
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(['dev', 'staging', 'prod'] as const).map((e) => (
                                <SegmentButton
                                    key={e}
                                    active={env === e}
                                    tone={e === 'prod' ? 'danger' : 'brand'}
                                    disabled={!!decided}
                                    onClick={() => setEnv(e)}
                                >
                                    {e === 'prod' ? 'production' : e}
                                </SegmentButton>
                            ))}
                        </Box>
                    </Box>
                )}

                {m.showOnError && (
                    <Box sx={{ mb: 1.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--main-text-secondary, #64748b)', mb: 1 }}>
                            If a task fails
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <SegmentButton
                                active={onerror === 'stop'}
                                tone="brand"
                                disabled={!!decided}
                                onClick={() => setOnerror('stop')}
                            >
                                Stop the run
                            </SegmentButton>
                            <SegmentButton
                                active={onerror === 'continue'}
                                tone="brand"
                                disabled={!!decided}
                                onClick={() => setOnerror('continue')}
                            >
                                Keep going
                            </SegmentButton>
                        </Box>
                    </Box>
                )}

                <Typography sx={{ fontSize: 13, color: 'var(--main-text-secondary, #64748b)', mb: 2, lineHeight: 1.5 }}>
                    {impactBody}
                </Typography>

                {!decided && needsTyped && (
                    <Box sx={{ mb: 1.75 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--main-text-secondary, #64748b)', mb: 1 }}>
                            {`Type "${m.systemName}" to confirm a production live run`}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={typedName}
                            onChange={(e) => setTypedName(e.target.value)}
                            placeholder={m.systemName || 'system name'}
                            inputProps={{ 'aria-label': 'Type the system name to confirm' }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    background: 'var(--main-bg-elevated, #f8fafc)',
                                },
                            }}
                        />
                    </Box>
                )}

                {decided ? (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            p: 1.75,
                            borderRadius: '12px',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            background: decided === 'approved'
                                ? 'color-mix(in srgb, var(--success-color, #22c55e) 12%, var(--card-bg, #fff))'
                                : 'var(--main-bg-elevated, #f8fafc)',
                        }}
                    >
                        {decided === 'approved' ? (
                            <CheckIcon sx={{ color: 'var(--success-color, #22c55e)' }} />
                        ) : (
                            <BlockIcon sx={{ color: 'var(--main-text-secondary, #64748b)' }} />
                        )}
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                {decided === 'approved'
                                    ? 'Approved — running the plan'
                                    : 'Declined — nothing executed'}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--main-text-secondary, #64748b)', mt: 0.5 }}>
                                {decided === 'approved'
                                    ? isLive
                                        ? `Live run started${env ? ` in ${env}` : ''}.`
                                        : `Dry run started${env ? ` in ${env}` : ''}.`
                                    : 'The run stays parked. No connector calls were made.'}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={understood}
                                    onChange={(e) => setUnderstood(e.target.checked)}
                                    sx={{
                                        color: 'var(--main-text-secondary, #64748b)',
                                        '&.Mui-checked': {
                                            color: isLive
                                                ? 'var(--error-color, #ef4444)'
                                                : 'var(--button-primary-bg, var(--quantum-violet, #6C4CFF))',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 13, color: 'var(--main-text-secondary, #64748b)' }}>
                                    {`I understand this run will use the settings above${isLive ? ' and may write real data' : ''}.`}
                                </Typography>
                            }
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                onClick={() => submit(false)}
                                disabled={isSubmitting}
                                data-testid="decline-button"
                                sx={{ textTransform: 'none', borderRadius: '10px' }}
                            >
                                Decline
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => submit(true)}
                                disabled={!gateReady || isSubmitting}
                                data-testid="approve-button"
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    background:
                                        'var(--button-primary-bg, var(--quantum-violet, #6C4CFF))',
                                }}
                            >
                                {isLive ? 'Approve live run' : 'Approve & run'}
                            </Button>
                            {!gateReady && (
                                <Typography sx={{ fontSize: 12, color: 'var(--main-text-secondary, #64748b)' }}>
                                    {needsTyped && !typedOk
                                        ? `Type "${m.systemName}" to enable.`
                                        : 'Confirm all required choices above to enable.'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default ConfirmationApprovalCard
